import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, Truck, Plus, Check, X, AlertCircle, Search, Mail, Phone, Globe, Pencil, Trash2, Package, Warehouse, ClipboardList, Upload, AlertOctagon } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { suppliersAPI } from '../services/api';
import PurchaseOrderModal from './PurchaseOrderModal';
import PurchaseOrderHistory from './PurchaseOrderHistory';
import PriceListImportModal from './PriceListImportModal';

const EMPTY = { name: '', contact_name: '', email: '', phone: '', website: '', lead_time_days: 3, notes: '', active: true };

const SupplierForm = ({ initial, onClose, onSaved }) => {
  const [form, setForm] = useState({ ...EMPTY, ...(initial || {}) });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const save = async () => {
    setError('');
    if (form.name.trim().length < 2) { setError('Le nom du fournisseur est obligatoire'); return; }
    setSaving(true);
    try {
      const payload = { ...form, name: form.name.trim(), lead_time_days: Math.max(0, parseInt(form.lead_time_days, 10) || 0) };
      const saved = initial?.id ? await suppliersAPI.update(initial.id, payload) : await suppliersAPI.create(payload);
      onSaved(saved);
    } catch (e) {
      setError(e.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden" data-testid="supplier-form">
        <div className="p-5 border-b bg-gradient-to-r from-orange-500 to-orange-600 text-white flex items-center justify-between">
          <h2 className="text-lg font-bold flex items-center gap-2" data-testid="supplier-form-title"><Truck size={20} /> {initial?.id ? `Modifier ${initial.name}` : 'Nouveau fournisseur'}</h2>
          <button onClick={onClose} className="text-white/80 hover:text-white"><X size={22} /></button>
        </div>
        <div className="p-6 overflow-y-auto flex-1 space-y-4">
          {error && <p data-testid="supplier-form-error" className="text-sm text-red-600 flex items-center gap-2"><AlertCircle size={16} /> {error}</p>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Nom du fournisseur *</label>
              <Input data-testid="supplier-name-input" value={form.name} onChange={set('name')} placeholder="Bardahl Distribution" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Contact</label>
              <Input data-testid="supplier-contact-input" value={form.contact_name} onChange={set('contact_name')} placeholder="Jean Dupont" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Délai de livraison (jours)</label>
              <Input data-testid="supplier-lead-input" type="number" min="0" max="90" value={form.lead_time_days} onChange={set('lead_time_days')} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">E-mail</label>
              <Input data-testid="supplier-email-input" type="email" value={form.email} onChange={set('email')} placeholder="pro@fournisseur.fr" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Téléphone</label>
              <Input data-testid="supplier-phone-input" value={form.phone} onChange={set('phone')} placeholder="01 23 45 67 89" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Site web</label>
              <Input data-testid="supplier-website-input" value={form.website} onChange={set('website')} placeholder="https://…" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Notes internes</label>
              <textarea data-testid="supplier-notes-input" value={form.notes} onChange={set('notes')} rows={3} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="Conditions, minimum de commande, remises…" />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" data-testid="supplier-active-input" checked={form.active} onChange={set('active')} className="rounded border-gray-300 text-orange-500" /> Fournisseur actif
            </label>
          </div>
        </div>
        <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} data-testid="supplier-form-cancel">Annuler</Button>
          <Button data-testid="supplier-form-submit" onClick={save} disabled={saving} className="bg-green-600 hover:bg-green-700">
            {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Check size={16} className="mr-2" />} Enregistrer
          </Button>
        </div>
      </div>
    </div>
  );
};

const SupplierProducts = ({ supplierId, onEditProduct }) => {
  const [data, setData] = useState(null);
  useEffect(() => { suppliersAPI.getById(supplierId).then(setData).catch(() => setData({ products: [] })); }, [supplierId]);
  if (!data) return <div className="p-4"><Loader2 className="animate-spin text-orange-500" size={18} /></div>;
  if (!data.products.length) return <p className="px-5 py-3 text-sm text-gray-500" data-testid="supplier-products-empty">Aucun produit lié. Ajoutez ce fournisseur depuis la fiche d'un produit (section « Fournisseurs & stock »).</p>;
  return (
    <div className="overflow-x-auto"><table className="rt-cards w-full text-sm" data-testid="supplier-products-table">
      <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
        <tr>
          <th className="px-4 py-2 text-left">Produit</th>
          <th className="px-4 py-2 text-left">Réf. fournisseur</th>
          <th className="px-4 py-2 text-right">Prix achat HT</th>
          <th className="px-4 py-2 text-right">Prix vente TTC</th>
          <th className="px-4 py-2 text-right">Stock entrepôt</th>
          <th className="px-4 py-2 text-right">Stock fournisseur</th>
          <th className="px-4 py-2" />
        </tr>
      </thead>
      <tbody className="divide-y">
        {data.products.map(p => (
          <tr key={p.id} data-testid={`supplier-product-${p.id}`}>
            <td data-label="Produit" className="px-4 py-2">
              <div className="flex items-center gap-2">
                <img src={p.image} alt="" className="w-9 h-9 object-contain bg-gray-50 rounded" />
                <div><p className="font-medium text-gray-900">{p.name}</p><p className="text-xs text-gray-400">{p.brand} · Réf. {p.ref}</p></div>
              </div>
            </td>
            <td data-label="Réf. fournisseur" className="px-4 py-2 font-mono text-xs">{p.supplier_ref || '—'}</td>
            <td data-label="Prix achat HT" className="px-4 py-2 text-right">{p.purchase_price != null ? `${Number(p.purchase_price).toFixed(2)} €` : '—'}</td>
            <td data-label="Prix vente TTC" className="px-4 py-2 text-right">{Number(p.price).toFixed(2)} €</td>
            <td data-label="Stock entrepôt" className="px-4 py-2 text-right">{p.warehouse_stock}</td>
            <td data-label="Stock fournisseur" className="px-4 py-2 text-right font-semibold">{p.supplier_stock}</td>
            <td className="px-4 py-2 text-right"><button type="button" data-testid={`supplier-product-edit-${p.id}`} onClick={() => onEditProduct(p.id)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="Modifier le produit"><Pencil size={14} /></button></td>
          </tr>
        ))}
      </tbody>
    </table></div>
  );
};

const AdminSuppliersPanel = ({ onEditProduct, onStockChanged }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [editing, setEditing] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [ordering, setOrdering] = useState(null);
  const [importing, setImporting] = useState(null);
  const [poRefresh, setPoRefresh] = useState(0);

  const load = useCallback(async () => {
    try { setSuppliers(await suppliersAPI.getAll()); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  // Deep link from a low-stock alert: /admin?tab=suppliers&supplier=<id>&reorder=1 → expand the supplier and open its purchase order
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get('supplier');
    if (!sid || !suppliers.length) return;
    const s = suppliers.find(x => x.id === sid);
    if (s) { setExpanded(s.id); if (params.get('reorder')) setOrdering(s); }
    params.delete('supplier'); params.delete('reorder');
    window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
  }, [suppliers]);

  const remove = async (s) => {
    if (!window.confirm(`Supprimer le fournisseur ${s.name} ?${s.product_count ? ` Il sera délié de ${s.product_count} produit(s) et son stock ne sera plus vendable.` : ''}`)) return;
    try {
      await suppliersAPI.delete(s.id);
      toast.success(`Fournisseur ${s.name} supprimé`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Suppression impossible');
    }
  };

  const visible = suppliers.filter(s => [s.name, s.contact_name, s.email].some(v => v && v.toLowerCase().includes(filter.toLowerCase())));
  const totalStock = suppliers.reduce((n, s) => n + (s.total_stock || 0), 0);
  const lateCount = suppliers.reduce((n, s) => n + (s.late_po_count || 0), 0);

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Fournisseurs ({suppliers.length})</h1>
          <p className="text-sm text-gray-500" data-testid="suppliers-summary">{totalStock} unité{totalStock > 1 ? 's' : ''} en stock chez vos fournisseurs · {suppliers.filter(s => s.active).length} actif{suppliers.filter(s => s.active).length > 1 ? 's' : ''}
            {lateCount > 0 && <span className="ml-2 inline-flex items-center gap-1 bg-red-100 text-red-700 font-semibold rounded-full px-2 py-0.5 text-xs" data-testid="suppliers-late-count"><AlertOctagon size={12} /> {lateCount} bon{lateCount > 1 ? 's' : ''} de commande en retard</span>}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[10rem]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input data-testid="suppliers-filter-input" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filtrer…" className="pl-9 w-full sm:w-56" />
          </div>
          <Button data-testid="supplier-new-button" onClick={() => setEditing({})} className="bg-orange-500 hover:bg-orange-600"><Plus size={16} className="mr-1" /> Nouveau fournisseur</Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-orange-500" /></div>
      ) : suppliers.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center" data-testid="suppliers-empty">
          <Truck size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-600 font-medium">Aucun fournisseur pour l'instant</p>
          <p className="text-sm text-gray-500 mt-1">Créez vos fournisseurs puis liez-les à vos produits pour vendre aussi leur stock (expédié sous X jours).</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="suppliers-list">
          {visible.map(s => (
            <div key={s.id} className={`bg-white rounded-lg shadow-sm ${!s.active ? 'opacity-70' : ''}`} data-testid={`supplier-card-${s.id}`}>
              <div className="p-4 flex flex-wrap items-center gap-4">
                <button type="button" onClick={() => setExpanded(expanded === s.id ? null : s.id)} className="flex-1 min-w-[200px] text-left" data-testid={`supplier-toggle-${s.id}`}>
                  <p className="font-semibold text-gray-900 flex items-center gap-2">
                    {s.name}
                    {!s.active && <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Inactif</span>}
                    {s.late_po_count > 0 && <span className="text-[10px] bg-red-100 text-red-700 font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1" data-testid={`supplier-late-badge-${s.id}`}><AlertOctagon size={10} /> {s.late_po_count} BC en retard</span>}
                  </p>
                  <p className="text-xs text-gray-500 flex flex-wrap gap-3 mt-1">
                    {s.contact_name && <span>{s.contact_name}</span>}
                    {s.email && <span className="flex items-center gap-1"><Mail size={11} /> {s.email}</span>}
                    {s.phone && <span className="flex items-center gap-1"><Phone size={11} /> {s.phone}</span>}
                    {s.website && <span className="flex items-center gap-1"><Globe size={11} /> {s.website.replace(/^https?:\/\//, '')}</span>}
                  </p>
                </button>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-center"><span className="block text-xs text-gray-500">Délai</span><strong>{s.lead_time_days} j</strong></span>
                  <span className="text-center"><span className="block text-xs text-gray-500">Produits</span><strong data-testid={`supplier-products-count-${s.id}`}>{s.product_count}</strong></span>
                  <span className="text-center"><span className="block text-xs text-gray-500">Stock</span><strong data-testid={`supplier-stock-${s.id}`}>{s.total_stock}</strong></span>
                </div>
                <div className="flex items-center gap-1">
                  <button type="button" data-testid={`supplier-import-${s.id}`} onClick={() => setImporting(s)} className="inline-flex items-center gap-1 text-xs font-medium text-[#1e3a5f] border border-[#1e3a5f]/30 hover:bg-blue-50 rounded-md px-2.5 py-1.5" title="Importer un tarif fournisseur (CSV / Excel)"><Upload size={14} /> Importer un tarif</button>
                  <button type="button" data-testid={`supplier-order-${s.id}`} onClick={() => setOrdering(s)} className="inline-flex items-center gap-1 text-xs font-medium text-white bg-[#1e3a5f] hover:bg-[#2d4a6f] rounded-md px-2.5 py-1.5" title="Générer un bon de commande (réassort stock bas)"><ClipboardList size={14} /> Bon de commande</button>
                  <button type="button" data-testid={`supplier-edit-${s.id}`} onClick={() => setEditing(s)} className="p-2 text-blue-600 hover:bg-blue-50 rounded"><Pencil size={16} /></button>
                  <button type="button" data-testid={`supplier-delete-${s.id}`} onClick={() => remove(s)} className="p-2 text-red-600 hover:bg-red-50 rounded"><Trash2 size={16} /></button>
                </div>
              </div>
              {expanded === s.id && (
                <div className="border-t" data-testid={`supplier-detail-${s.id}`}>
                  {s.notes && <p className="px-5 py-2 text-xs text-gray-600 bg-amber-50/60 border-b">{s.notes}</p>}
                  <p className="px-5 pt-3 text-xs font-semibold text-gray-500 uppercase flex items-center gap-1"><Package size={12} /> Produits fournis</p>
                  <SupplierProducts supplierId={s.id} onEditProduct={onEditProduct} />
                  <p className="px-5 pt-3 text-xs font-semibold text-gray-500 uppercase flex items-center gap-1 border-t mt-2"><ClipboardList size={12} /> Bons de commande</p>
                  <PurchaseOrderHistory supplierId={s.id} refreshKey={poRefresh} onReceived={() => { load(); onStockChanged && onStockChanged(); }} />
                </div>
              )}
            </div>
          ))}
          {visible.length === 0 && <p className="text-sm text-gray-500">Aucun fournisseur ne correspond.</p>}
        </div>
      )}

      <p className="mt-6 text-xs text-gray-500 flex items-center gap-1"><Warehouse size={12} /> Le stock entrepôt se gère sur chaque produit ; le stock fournisseur se saisit dans la section « Fournisseurs & stock » de la fiche produit.</p>

      {importing && <PriceListImportModal supplier={importing} onClose={() => setImporting(null)} onApplied={() => { load(); onStockChanged && onStockChanged(); }} />}
      {ordering && <PurchaseOrderModal supplier={ordering} onClose={() => setOrdering(null)} onCreated={() => { setOrdering(null); setExpanded(ordering.id); setPoRefresh(k => k + 1); }} />}
      {editing && <SupplierForm initial={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={(s) => { setEditing(null); toast.success(`Fournisseur ${s.name} enregistré`); load(); }} />}
    </>
  );
};

export default AdminSuppliersPanel;
